<template>
  <article class="work-record" :class="{ 'work-record-zh': zh }">
    <!-- heading：單曲頁（pageMusicTrack）把歌名搬進自己的「聽歌的房間」，這裡只留回到音樂（2026-10-04） -->
    <slot name="heading"><header class="work-record-heading" :class="{ 'has-gene': gene }">
      <router-link class="back-link work-back" :to="backPath"><brand-icon name="back" />{{ labels.back || (zh ? '返回作品檔案' : 'Back to work') }}</router-link>
      <div class="work-identity"><p class="work-kicker"><span v-if="category">{{ category }}</span><span v-if="year">{{ year }}</span></p><h1 :lang="titleLanguage">{{ title }}</h1><p v-if="subtitle" class="work-subtitle">{{ subtitle }}</p><gene-strip v-if="gene" :slug="gene" label class="work-gene" /></div>
    </header></slot>
    <!-- lead：頁首下方的主視覺。預設是作品封面；單曲頁（pageMusicTrack）換成「封面＋日期＋聽」並排（2026-10-04） -->
    <slot name="lead"><figure v-if="cover && !heroBlock" class="work-cover">
      <button type="button" @click="showImage(hero, $event.currentTarget)" :aria-label="zh ? '放大作品影像' : 'Enlarge artwork image'"><img class="vt-work-media" :src="cover" :alt="title" fetchpriority="high" decoding="async" /><span class="work-image-hint">{{ zh ? '放大影像' : 'View image' }} <svg class="work-icon-arrow" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M4 12 12 4M6.5 4H12v5.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></span></button>
      <figcaption v-if="coverCaption">{{ coverCaption }}</figcaption>
    </figure></slot>
    <template v-for="(run,ri) in runs" :key="'run-'+ri">
      <template v-if="run.full">
        <template v-for="block in run.blocks" :key="block.id">
        <section v-if="block.type==='hero'" :id="block.id" class="work-stage work-hero"><figure class="work-hero-figure"><span class="work-hero-media vt-work-media"><video :ref="setHeroVideo" v-if="block.src" :src="heroHd && block.srcHd ? block.srcHd : block.src" :poster="block.poster" muted loop playsinline preload="metadata" :width="block.width" :height="block.height" :style="block.width&&block.height?{aspectRatio:block.width+'/'+block.height}:null" :aria-label="block.caption || title" @click="toggleHero" @loadeddata="settleHero"></video><img v-else-if="block.poster" :src="block.poster" :alt="block.caption || title" :width="block.width" :height="block.height" fetchpriority="high" decoding="async" @load="settleHero" /><img v-if="underlay && cover" class="work-media-underlay" :class="{ 'is-settled': heroSettled }" :src="cover" alt="" aria-hidden="true" /></span><button v-if="block.src" type="button" class="work-hero-toggle" :aria-pressed="!heroPaused" @click="toggleHero">{{ heroPaused ? (zh ? '播放' : 'Play') : (zh ? '暫停' : 'Pause') }}</button><figcaption v-if="block.caption || block.note"><span :lang="block.lang">{{ block.caption }}</span><span v-if="block.note" class="work-hero-note">{{ block.note }}</span></figcaption></figure></section>
        <section v-else-if="block.type==='strip'" :id="block.id" class="work-stage work-strip-stage"><div class="work-gallery-heading"><span class="work-section-label">{{ block.title }}</span><span>{{ block.hint || (zh ? '左右拖曳或橫向捲動，看整條布' : 'Drag or scroll sideways to explore the whole cloth') }}</span></div><figure v-for="(item,i) in block.items" :key="item.src" class="work-strip"><div class="work-strip-scroll" tabindex="0" :aria-label="(item.caption || item.alt)+(zh ? '（可橫向捲動）' : ' (scrolls sideways)')" @scroll.passive="stripScroll($event,item.src)" @pointerdown="stripDown" @pointermove="stripMove" @pointerup="stripUp" @pointercancel="stripUp" @pointerleave="stripUp"><img :src="item.src" :alt="item.alt" :width="item.width" :height="item.height" :style="{height:block.height+'px'}" loading="lazy" decoding="async" draggable="false" /></div><div class="work-strip-bar" aria-hidden="true"><span :style="{width:stripThumb(item.src)+'%',left:stripPos(item.src)+'%'}"></span></div><figcaption><span>{{ item.caption }}</span><button type="button" class="work-strip-open" @click="showImage(item,$event.currentTarget)">{{ zh ? '整條放大' : 'Open whole strip' }} <svg class="work-icon-arrow" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M4 12 12 4M6.5 4H12v5.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></button></figcaption></figure></section>
        <figure v-else-if="block.type==='figure'" :id="block.id" class="work-stage work-figure-stage"><button type="button" @click="showImage(block,$event.currentTarget)" :aria-label="zh ? '放大影像' : 'Enlarge image'"><img :src="block.src" :alt="block.alt || title" :width="block.width" :height="block.height" loading="lazy" decoding="async" /><span class="work-image-hint"><svg class="work-icon-arrow" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M4 12 12 4M6.5 4H12v5.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></span></button><figcaption v-if="block.caption">{{ block.caption }}</figcaption></figure>
        <section v-else-if="block.type==='columns'" :id="block.id" class="work-stage work-stage-prose"><h2 v-if="block.title">{{ block.title }}</h2><div class="work-columns-grid"><div v-for="(col,i) in block.items" :key="i" class="work-column" :lang="col.lang"><h3 v-if="col.title">{{ col.title }}</h3><p v-for="(paragraph,j) in col.paragraphs" :key="j">{{ paragraph }}</p></div></div></section>
        <section v-else-if="block.type==='text'" :id="block.id" class="work-stage work-stage-prose"><h2>{{ block.title }}</h2><div :lang="block.lang"><p v-for="(paragraph,i) in block.paragraphs" :key="i">{{ paragraph }}</p></div></section>
        <figure v-else-if="block.type==='quote'" :id="block.id" class="work-stage work-stage-prose work-quote quote"><blockquote :lang="block.lang"><p v-for="(paragraph,i) in block.paragraphs" :key="i">{{ paragraph }}</p></blockquote><figcaption v-if="block.cite">{{ block.cite }}</figcaption></figure>
        <section v-else-if="block.type==='poem'" :id="block.id" class="work-stage work-poem"><p v-if="block.heading||block.cite" class="work-poem-head"><span v-if="block.heading" class="work-poem-title">{{ block.heading }}</span><span v-if="block.cite" class="work-poem-cite">{{ block.cite }}</span></p><div v-for="(version,k) in block.versions" :key="k" class="work-poem-version" :lang="version.lang"><p v-if="version.label" class="work-poem-label">{{ version.label }}</p><div class="work-poem-body" :class="'work-poem-cols-'+version.cols"><p v-for="(stanza,i) in version.stanzas" :key="i" class="work-poem-stanza"><template v-for="(line,j) in stanza" :key="j"><br v-if="j">{{ line }}</template></p></div></div><p v-if="block.note" class="work-poem-note">{{ block.note }}</p></section>
        <section v-else-if="block.type==='table'" :id="block.id" class="work-stage work-stage-prose work-table"><h2 v-if="block.title">{{ block.title }}</h2><div class="work-table-scroll"><table><thead v-if="block.columns.length"><tr><th v-for="(c,i) in block.columns" :key="i">{{ c }}</th></tr></thead><tbody><tr v-for="(r,i) in block.rows" :key="i"><td v-for="(c,j) in r" :key="j">{{ c }}</td></tr></tbody></table></div><p v-if="block.note" class="work-table-note">{{ block.note }}</p></section>
        <section v-else-if="block.type==='credits'" :id="block.id" class="work-stage work-stage-prose work-credits"><h2>{{ block.title }}</h2><dl><template v-for="(row,i) in block.rows" :key="i"><dt>{{ row.label }}</dt><dd>{{ row.value }}</dd></template></dl></section>
        <section v-else-if="block.type==='media'" :id="block.id" class="work-stage"><div class="work-gallery-heading"><span class="work-section-label">{{ block.title }}</span></div><figure v-for="item in block.items" :key="item.src" class="work-media work-media-stage"><iframe :src="item.src" :title="item.title || title" loading="lazy" :allow="mediaAllow(item.src)" allowfullscreen class="work-loaded-media"></iframe><figcaption><span>{{ item.title || (zh ? '影像紀錄' : 'Documentation') }}</span><a :href="item.src" target="_blank" rel="noopener noreferrer">{{ zh ? '在新分頁開啟' : 'Open in a new tab' }} <svg class="work-icon-arrow" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M4 12 12 4M6.5 4H12v5.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></a></figcaption></figure></section>
        <section v-else-if="block.type==='loops'" :id="block.id" class="work-stage work-loops-stage"><div class="work-gallery-heading"><span class="work-section-label">{{ block.title }}</span><span>{{ block.items.length }}</span></div><div class="work-loops-grid" :class="'work-loops-cols-'+block.columns"><figure v-for="item in block.items" :key="item.src||item.poster" class="work-loop"><video v-if="item.src && !loopsStill" :data-src="item.src" :poster="item.poster" muted loop playsinline preload="none" :width="item.width" :height="item.height" :aria-label="item.alt || item.caption || title" @play.once="videoEvent('work_loop_play',item)"></video><img v-else :src="item.poster || item.src" :alt="item.alt || item.caption || title" :width="item.width" :height="item.height" loading="lazy" decoding="async" /><figcaption v-if="item.caption" :lang="item.lang">{{ item.caption }}</figcaption></figure></div><p v-if="block.note" class="work-loops-note">{{ block.note }}</p></section>
        <section v-else-if="block.type==='video'" :id="block.id" class="work-stage work-video-stage"><div class="work-gallery-heading"><span class="work-section-label">{{ block.title }}</span><span v-if="block.seconds">{{ Math.round(block.seconds) }}s</span></div><figure class="work-video" :class="{'work-video-portrait':block.height>block.width}"><video v-if="block.src" :src="block.src" :poster="block.poster" controls playsinline preload="metadata" :muted="!block.audio || (block.autoplay!==false && !soundOn[block.id])" :loop="!block.audio" :data-autoplay="block.autoplay===false?null:'1'" :width="block.width" :height="block.height" @play.once="videoEvent('td_video_play',block)" @ended.once="videoEvent('td_video_complete',block)" @pause="videoPaused" @play="videoPlayed"></video><button v-if="block.src && block.audio && block.autoplay!==false" type="button" class="work-video-sound" :aria-pressed="!!soundOn[block.id]" @click="toggleSound(block,$event)">{{ soundOn[block.id] ? (zh ? '關聲音' : 'Mute') : (zh ? '開聲音' : 'Sound on') }}</button><img v-if="!block.src && block.poster" :src="block.poster" :alt="title" :width="block.width" :height="block.height" loading="lazy" /><figcaption><span>{{ block.caption || title }}</span><span v-if="!block.src" class="work-video-pending">{{ zh ? '影片整理中，先看定格' : 'Film on its way; a still for now' }}</span></figcaption></figure></section>
        <section v-else-if="block.type==='lineage'" :id="block.id" class="work-stage work-lineage-stage"><div class="work-gallery-heading"><span class="work-section-label">{{ block.title }}</span></div><lineage-block :sketch="block.sketch" :mode="block.mode" :subject="block.subject" :zh="zh" /></section>
        <section v-else-if="block.type==='row'" :id="block.id" class="work-stage work-row-stage"><div v-if="block.title" class="work-gallery-heading"><span class="work-section-label">{{ block.title }}</span></div><div class="work-row-grid" :style="{gridTemplateColumns:'repeat('+block.columns+',minmax(0,1fr))',alignItems:block.align,'--row-mobile-cols':block.mobileColumns||1}"><template v-for="(item,i) in block.items" :key="i"><figure v-if="item.kind==='image'" class="work-row-image" :style="{gridColumn:item.span?'span '+item.span:null}"><button type="button" @click="showImage(item,$event.currentTarget)" :aria-label="zh ? '放大影像' : 'Enlarge image'"><img :src="item.src" :alt="item.alt || title" :width="item.width" :height="item.height" :style="item.width&&item.height?{aspectRatio:item.width+'/'+item.height}:null" loading="lazy" decoding="async" /><span class="work-image-hint"><svg class="work-icon-arrow" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M4 12 12 4M6.5 4H12v5.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></span></button><figcaption v-if="item.caption">{{ item.caption }}</figcaption></figure><div v-else class="work-row-text" :style="{gridColumn:item.span?'span '+item.span:null}" :lang="item.lang"><h3 v-if="item.title">{{ item.title }}</h3><p v-for="(paragraph,j) in item.paragraphs" :key="j">{{ paragraph }}</p></div></template></div></section>
        <section v-else-if="block.type==='flow'" :id="block.id" class="work-stage work-flow-stage" :class="'work-flow-'+(block.variant||'acts')"><div class="work-gallery-heading"><span class="work-section-label">{{ block.title }}</span><span>{{ block.steps.length }}</span></div><ol class="work-flow"><li v-for="(s,i) in block.steps" :key="i" class="work-flow-step" :style="{'--tone':s.color}"><div class="work-flow-head"><span class="work-flow-index">{{ s.index || String(i+1).padStart(2,'0') }}</span><h3 class="work-flow-name" :lang="block.lang">{{ s.name }}</h3></div><p v-if="s.note" class="work-flow-note" :lang="block.lang">{{ s.note }}</p><ul v-if="s.items&&s.items.length" class="work-flow-items"><li v-for="(it,j) in s.items" :key="j" :class="{'is-key':it.key}" :lang="block.lang">{{ it.text }}</li></ul><div v-if="s.people||s.world" class="work-flow-hands"><p v-if="s.people"><span>{{ block.labels.people }}</span><span :lang="block.lang">{{ s.people }}</span></p><p v-if="s.world"><span>{{ block.labels.world }}</span><span :lang="block.lang">{{ s.world }}</span></p></div></li></ol><div v-if="(block.legend&&block.legend.length)||block.note" class="work-flow-foot"><ul v-if="block.legend&&block.legend.length" class="work-flow-legend"><li v-for="(l,i) in block.legend" :key="i" :style="{'--tone':l.color}">{{ l.label }}</li></ul><p v-if="block.note" class="work-flow-loop" :lang="block.lang">{{ block.note }}</p></div></section>
        <section v-else-if="block.type==='genome'" :id="block.id" class="work-stage work-genome-stage"><div class="work-gallery-heading"><span class="work-section-label">{{ block.title }}</span><span>{{ block.items.length }}</span></div><div class="work-genome-grid" :class="'work-genome-cols-'+(block.columns||4)"><genome-card v-for="it in block.items" :key="it.hash" :item="it" :zh="zh" :square="!!block.square" /></div><p v-if="block.note" class="work-loops-note">{{ block.note }}</p></section>
        <work-zoom v-else-if="block.type==='zoom'" :block="block" :zh="zh" />
        <work-board v-else-if="block.type==='board'" :id="block.id" :title="block.title" :note="block.note" :items="block.items" :columns="block.columns" />
        <section v-else :id="block.id" class="work-gallery" :class="['work-gallery-fit-'+(block.fit||'contain'),{'work-gallery-mosaic':block.layout==='mosaic'}]"><div class="work-gallery-heading"><span class="work-section-label">{{ block.title }}</span><span>{{ block.items.length }}</span></div><div class="work-gallery-grid" :class="'work-gallery-cols-'+block.columns" :style="block.layout!=='mosaic'&&block.columns>3?{gridTemplateColumns:'repeat('+block.columns+',minmax(0,1fr))'}:null"><figure v-for="(item,i) in block.items" :key="item.src" :class="{'work-gallery-wide':item.wide&&block.layout!=='mosaic','work-gallery-natural':item.width&&item.height&&block.layout!=='mosaic'}" :style="block.layout==='mosaic'?{'--s':item.cols,'--r':item.rows}:{gridColumn:item.span?'span '+item.span:null}"><button type="button" @click="showImage(item,$event.currentTarget)" :aria-label="(item.caption||item.alt||(zh ? '放大影像 ' : 'Enlarge image ')+(i+1))"><img :src="item.src" :alt="item.alt || title" :width="item.width" :height="item.height" :style="item.width&&item.height&&block.layout!=='mosaic'?{aspectRatio:item.width+'/'+item.height}:null" loading="lazy" decoding="async" /><span class="work-image-hint"><svg class="work-icon-arrow" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M4 12 12 4M6.5 4H12v5.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></span></button><figcaption v-if="item.caption&&block.layout!=='mosaic'">{{ item.caption }}</figcaption></figure></div><p v-if="block.note" class="work-gallery-note">{{ block.note }}</p></section>
        </template>
      </template>
      <div v-else class="work-reading-layout" :class="{'work-context-layout':!run.lead}">
        <aside v-if="run.lead" class="work-record-aside">
          <nav v-if="outline.length" :aria-label="labels.contents || (zh ? '作品目錄' : 'Work contents')"><a v-for="(item,i) in outline" :key="item.id" :href="'#'+item.id"><span>{{ String(i+1).padStart(2,'0') }}</span>{{ item.title }}</a></nav>
          <dl v-if="facts.length"><template v-for="(fact,i) in facts" :key="i"><dt>{{ fact.label }}</dt><dd><router-link v-if="fact.href && fact.href.startsWith('/')" :to="fact.href">{{ fact.value }}</router-link><a v-else-if="fact.href" :href="fact.href" target="_blank" rel="noopener">{{ fact.value }}</a><template v-else>{{ fact.value }}</template></dd></template></dl>
          <a v-if="experiencePath" class="work-experience" :href="experiencePath">{{ zh ? '原始專頁與互動體驗' : 'Original page & interactive experience' }} <svg class="work-icon-arrow" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M4 12 12 4M6.5 4H12v5.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></a>
        </aside>
        <div v-else aria-hidden="true"></div>
        <div class="work-reading">
          <details v-if="run.lead" class="work-mobile-index"><summary>{{ labels.details || (zh ? '目錄與作品資料' : 'Contents & work details') }}</summary><nav :aria-label="labels.contents || (zh ? '作品目錄' : 'Work contents')"><a v-for="item in outline" :key="item.id" :href="'#'+item.id">{{ item.title }}</a></nav><dl><template v-for="(fact,i) in facts" :key="i"><dt>{{ fact.label }}</dt><dd>{{ fact.value }}</dd></template></dl><a v-if="experiencePath" :href="experiencePath">{{ zh ? '原始專頁與互動體驗' : 'Original page & interactive experience' }} <svg class="work-icon-arrow" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M4 12 12 4M6.5 4H12v5.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></a></details>
          <template v-for="block in run.blocks" :key="block.id">
            <section v-if="block.type==='intro'" :id="block.id" class="work-text-section"><p class="work-section-label">{{ block.title }}</p><div class="work-intro" :lang="block.lang"><p v-for="(paragraph,i) in block.paragraphs" :key="i">{{ paragraph }}</p></div></section>
            <section v-else-if="block.type==='text'" :id="block.id" class="work-text-section"><h2>{{ block.title }}</h2><div :lang="block.lang"><p v-for="(paragraph,i) in block.paragraphs" :key="i">{{ paragraph }}</p></div></section>
            <section v-else-if="block.type==='columns'" :id="block.id" class="work-text-section work-columns"><h2 v-if="block.title">{{ block.title }}</h2><div class="work-columns-grid"><div v-for="(col,i) in block.items" :key="i" class="work-column" :lang="col.lang"><h3 v-if="col.title">{{ col.title }}</h3><p v-for="(paragraph,j) in col.paragraphs" :key="j">{{ paragraph }}</p></div></div></section>
            <section v-else-if="block.type==='html'" :id="block.id" class="work-text-section work-original"><details class="work-original-details"><summary><h2>{{ block.title }}</h2><p v-if="block.note" class="work-language-note">{{ block.note }}</p><span class="work-original-toggle" aria-hidden="true"><span class="work-original-show">{{ zh ? '＋ 展開全文' : '+ Read it' }}</span><span class="work-original-hide">{{ zh ? '－ 收起' : '− Hide' }}</span></span></summary><work-rich-text :html="block.html" :lang="block.lang" :zh="zh" :title="title" @image="showRichImage" /></details></section>
            <figure v-else-if="block.type==='quote'" :id="block.id" class="work-quote quote"><blockquote :lang="block.lang"><p v-for="(paragraph,i) in block.paragraphs" :key="i">{{ paragraph }}</p></blockquote><figcaption v-if="block.cite">{{ block.cite }}</figcaption></figure>
            <section v-else-if="block.type==='audio'" :id="block.id" class="work-text-section work-audio"><h2>{{ block.title }}</h2><p v-if="block.caption" class="work-audio-caption">{{ block.caption }}</p><audio :ref="el=>setAudio(block.id,el)" :src="block.src" controls preload="none" @timeupdate="audioTick(block.id,$event)" @play.once="videoEvent('work_audio_play',block)"></audio><ol v-if="block.chapters.length" class="work-audio-chapters" :lang="block.lang"><li v-for="(c,i) in block.chapters" :key="i" :class="{'is-current':audioChapter(block,i)}"><button type="button" @click="seekAudio(block.id,c.t)" :aria-label="(zh?'從 ':'Play from ')+clock(c.t)+' '+c.title"><span class="work-audio-time">{{ clock(c.t) }}</span><span class="work-audio-name">{{ c.title }}</span></button><p v-if="c.note" class="work-audio-note">{{ c.note }}</p></li></ol><p v-if="block.note" class="work-audio-foot">{{ block.note }}</p></section>
            <section v-else-if="block.type==='poem'" :id="block.id" class="work-poem"><p v-if="block.heading||block.cite" class="work-poem-head"><span v-if="block.heading" class="work-poem-title">{{ block.heading }}</span><span v-if="block.cite" class="work-poem-cite">{{ block.cite }}</span></p><div v-for="(version,k) in block.versions" :key="k" class="work-poem-version" :lang="version.lang"><p v-if="version.label" class="work-poem-label">{{ version.label }}</p><div class="work-poem-body" :class="'work-poem-cols-'+version.cols"><p v-for="(stanza,i) in version.stanzas" :key="i" class="work-poem-stanza"><template v-for="(line,j) in stanza" :key="j"><br v-if="j">{{ line }}</template></p></div></div><p v-if="block.note" class="work-poem-note">{{ block.note }}</p></section>
            <figure v-else-if="block.type==='figure'" :id="block.id" class="work-inline-figure" :class="{'work-inline-wide':block.wide}"><button type="button" @click="showImage(block,$event.currentTarget)" :aria-label="zh ? '放大影像' : 'Enlarge image'"><img :src="block.src" :alt="block.alt || title" loading="lazy" decoding="async" /><span class="work-image-hint"><svg class="work-icon-arrow" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M4 12 12 4M6.5 4H12v5.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></span></button><figcaption v-if="block.caption">{{ block.caption }}</figcaption></figure>
            <section v-else-if="block.type==='table'" :id="block.id" class="work-text-section work-table"><h2 v-if="block.title">{{ block.title }}</h2><div class="work-table-scroll"><table><thead v-if="block.columns.length"><tr><th v-for="(c,i) in block.columns" :key="i">{{ c }}</th></tr></thead><tbody><tr v-for="(r,i) in block.rows" :key="i"><td v-for="(c,j) in r" :key="j">{{ c }}</td></tr></tbody></table></div><p v-if="block.note" class="work-table-note">{{ block.note }}</p></section>
            <section v-else-if="block.type==='credits'" :id="block.id" class="work-text-section work-credits"><h2>{{ block.title }}</h2><dl><template v-for="(row,i) in block.rows" :key="i"><dt>{{ row.label }}</dt><dd>{{ row.value }}</dd></template></dl></section>
            <section v-else-if="block.type==='video'" :id="block.id" class="work-text-section work-video-section"><h2>{{ block.title }}</h2><figure class="work-video" :class="{'work-video-portrait':block.height>block.width}"><video v-if="block.src" :src="block.src" :poster="block.poster" controls playsinline preload="metadata" :muted="!block.audio || (block.autoplay!==false && !soundOn[block.id])" :loop="!block.audio" :data-autoplay="block.autoplay===false?null:'1'" :width="block.width" :height="block.height" @play.once="videoEvent('td_video_play',block)" @ended.once="videoEvent('td_video_complete',block)" @pause="videoPaused" @play="videoPlayed"></video><button v-if="block.src && block.audio && block.autoplay!==false" type="button" class="work-video-sound" :aria-pressed="!!soundOn[block.id]" @click="toggleSound(block,$event)">{{ soundOn[block.id] ? (zh ? '關聲音' : 'Mute') : (zh ? '開聲音' : 'Sound on') }}</button><img v-if="!block.src && block.poster" :src="block.poster" :alt="title" :width="block.width" :height="block.height" loading="lazy" /><figcaption><span>{{ block.caption || title }}</span><span v-if="!block.src" class="work-video-pending">{{ zh ? '影片整理中，先看定格' : 'Film on its way; a still for now' }}</span></figcaption></figure></section>
            <section v-else-if="block.type==='lineage'" :id="block.id" class="work-text-section work-lineage"><h2>{{ block.title }}</h2><lineage-block :sketch="block.sketch" :mode="block.mode" :subject="block.subject" :zh="zh" /></section>
            <section v-else-if="block.type==='media'" :id="block.id" class="work-text-section"><h2>{{ block.title }}</h2><figure v-for="item in block.items" :key="item.src" class="work-media"><iframe :src="item.src" :title="item.title || title" loading="lazy" :allow="mediaAllow(item.src)" allowfullscreen class="work-loaded-media"></iframe><figcaption><span>{{ item.title || (zh ? '影像紀錄' : 'Documentation') }}</span><a :href="item.src" target="_blank" rel="noopener noreferrer">{{ zh ? '在新分頁開啟' : 'Open in a new tab' }} <svg class="work-icon-arrow" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M4 12 12 4M6.5 4H12v5.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></a></figcaption></figure></section>
          </template>
        </div>
      </div>
    </template>
    <div class="work-reading-layout work-context-layout"><div aria-hidden="true"></div><div class="work-reading">
      <section v-if="links.length" id="context" class="work-text-section"><h2>{{ zh ? '延伸閱讀與來源' : 'Context & sources' }}</h2><ul class="work-source-list"><li v-for="(link,i) in links" :key="link.url+'-'+i"><router-link v-if="link.url.startsWith('/') && !link.url.startsWith('//')" :to="link.url">{{ link.label }} →</router-link><a v-else :href="link.url" target="_blank" rel="noopener noreferrer">{{ link.label }} <svg class="work-icon-arrow" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M4 12 12 4M6.5 4H12v5.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></a></li></ul></section>
      <section class="work-contact"><p>{{ zh ? '展出、研究與合作' : 'Exhibitions, research & collaborations' }}</p><router-link :to="contactPath">{{ zh ? '聯繫藝術家' : 'Contact the artist' }} <svg class="work-icon-arrow" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M4 12 12 4M6.5 4H12v5.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></router-link></section>
    </div></div>
    <section v-if="related.length" class="work-related"><h2>{{ labels.related || (zh ? '沿著作品繼續閱讀' : 'Continue through the work') }}</h2><div class="work-related-grid"><router-link v-for="item in related" :key="item.path" :to="item.path"><img v-if="item.cover" :src="item.cover" :alt="item.title" loading="lazy" /><p>{{ item.year }}</p><h3>{{ item.title }}</h3></router-link></div></section>
    <site-links inset />
    <nav v-if="previous || next" class="work-neighbours" :aria-label="labels.neighbours || (zh ? '作品前後頁' : 'Adjacent works')"><router-link v-if="previous" :to="previous.path"><span>← {{ labels.previous || (zh ? '上一件作品' : 'Previous work') }}</span><strong>{{ previous.title }}</strong></router-link><router-link v-if="next" :to="next.path" class="work-next"><span>{{ labels.next || (zh ? '下一件作品' : 'Next work') }} →</span><strong>{{ next.title }}</strong></router-link></nav>
    <work-lightbox ref="lightbox" :zh="zh" />
  </article>
</template>
<script setup>
import { withSlash } from '@/runtime/site-path.mjs'
import {computed, ref, reactive, onMounted, onBeforeUnmount, watch, nextTick} from 'vue'
import { useRoute } from 'vue-router'
import SiteLinks from '@/components/ui/SiteLinks.vue'
import WorkLightbox from './WorkLightbox.vue'
import WorkRichText from './WorkRichText.vue'
import WorkBoard from './WorkBoard.vue'
import WorkZoom from './WorkZoom.vue'
import GenomeCard from './GenomeCard.vue'
import GeneStrip from '@/components/GeneStrip.vue'
import BrandIcon from '@/components/BrandIcon.vue'
import LineageBlock from '@/components/lineage/LineageBlock.vue'
import { lineageRef } from '@/components/lineage/lineage-ref.mjs'
import { mediaAllow } from '@/runtime/embed-allow.mjs'
import { trackEvent } from '@/runtime/analytics.mjs'
const props=defineProps({title:{type:String,required:true},subtitle:String,gene:String,category:String,year:[String,Number],cover:String,coverCaption:String,intro:String,introLanguage:{type:String,default:'en'},facts:{type:Array,default:()=>[]},sections:{type:Array,default:()=>[]},records:{type:Array,default:()=>[]},gallery:{type:Array,default:()=>[]},links:{type:Array,default:()=>[]},related:{type:Array,default:()=>[]},media:{type:Array,default:()=>[]},blocks:{type:Array,default:()=>[]},lineage:Object,mediaWide:Boolean,previous:Object,next:Object,experiencePath:String,backPath:{type:String,default:'/work/'},zh:Boolean,labels:{type:Object,default:()=>({})}})
const route=useRoute()
const contactPath=computed(()=>({path:local('/contact'),query:{work:props.title,from:route.path}}))
const lightbox=ref(null)
// 影片事件：只送公開目錄事實（標題、影片路徑），參數由 analytics.mjs 清過
const videoEvent=(name,block)=>trackEvent(name,{title:props.title,video_url:block.src,seconds:block.seconds})
const local=p=>withSlash((props.zh?'/zh':'')+p)
const titleLanguage=computed(()=>/[\u3400-\u9fff]/.test(props.title)?'zh-Hant':'en')
const hero=computed(()=>({src:props.cover,alt:props.title,caption:props.coverCaption||''}))
const introParagraphs=computed(()=>props.intro?.split(/\n\s*\n|\n/).filter(p=>p.trim())||[])
// 版型＝一串區塊。一件作品可以自己排順序（work-details 的 blocks 欄），
// 沒排的就沿用舊欄位組出「概述→章節→作品原文→影片→影像紀錄」——跟改版前逐格相同。
const legacyBlocks=computed(()=>{
 const out=[]
 if(props.intro)out.push({type:'intro',id:'overview',title:props.zh?'作品概述':'Overview',paragraphs:introParagraphs.value,lang:props.introLanguage})
 for(const s of props.sections)out.push({type:'text',id:s.id,title:s.title,paragraphs:s.paragraphs,lang:s.lang})
 for(const r of props.records)out.push({type:'html',id:r.id,title:r.title,html:r.html,lang:r.lang,note:r.note})
 if(props.media.length)out.push({type:'media',id:'experience',title:props.zh?'影片與互動':'Films & interaction',items:props.media,wide:props.mediaWide})
 if(props.gallery.length)out.push({type:'gallery',id:'gallery',title:props.zh?'影像紀錄':'Images',items:props.gallery})
 return out
})
// 同一張圖不重複出現（封面已經在最上面了），排完才知道哪些 gallery 其實空了。
const prepared=computed(()=>{
 const seen=new Set(props.cover?[props.cover]:[])
 const out=[]
 for(const block of (props.blocks.length?props.blocks:legacyBlocks.value)){
  if(block.type!=='gallery'){out.push(block);continue}
  const items=[]
  for(const item of block.items||[]){if(!item.src||seen.has(item.src))continue;seen.add(item.src);items.push(item)}
  if(items.length)out.push({...block,items,columns:block.columns||2})
 }
 // 族譜區塊：頁面可以用 blocks 明排它的位置；沒排的話掛在最後——它是延伸閱讀，不是主體
 // sketch 是 OpenProcessing 的 visualID 或實驗室的 slug，LineageBlock 用 lineage-ref.mjs 解析（2026-10-05）
 if(props.lineage?.sketch&&!out.some(b=>b.type==='lineage')){
  const mode=props.lineage.mode||'strip'
  out.push({type:'lineage',id:'lineage',title:props.lineage.title||(props.zh?'從每日創作長出來':'Grown from the daily practice'),sketch:lineageRef(props.lineage.sketch),mode,subject:props.lineage.subject||'sketch',wide:mode==='tree'})
 }
 return out
})
// 整頁寬的區塊（影像紀錄）要跳出「側欄＋正文」兩欄，所以把相鄰的區塊分段。
// 只有第一段正文帶側欄；後面的段落用一個空欄位對齊，沿用既有的 work-context-layout。
// 哪些區塊跳出「側欄＋正文」兩欄、吃整頁寬：畫廊／頁首影片／長條探索天生滿寬；
// 其他任何區塊（圖／正文／引言／並排欄位／製作團隊）帶 full:true 也可以。版型是資料排的，不是元件決定的（2026-09-18）。
const FULL_WIDTH=new Set(['gallery','hero','strip','loops','row','flow','genome','board','zoom'])
const isFull=b=>FULL_WIDTH.has(b.type)||!!b.full||((b.type==='media'||b.type==='lineage'||b.type==='video')&&b.wide)
const heroBlock=computed(()=>prepared.value.find(b=>b.type==='hero'))
const runs=computed(()=>{
 const out=[]
 for(const block of prepared.value){
  const full=isFull(block),last=out[out.length-1]
  if(last&&last.full===full)last.blocks.push(block);else out.push({full,blocks:[block],lead:false})
 }
 const firstReading=out.find(r=>!r.full)
 if(firstReading)firstReading.lead=true
 else out.unshift({full:false,blocks:[],lead:true})   // 全是圖也要有地方放作品資料
 return out
})
const allImages=computed(()=>{
 const out=props.cover?[hero.value]:[]
 for(const block of prepared.value){
  if(block.type==='gallery'||block.type==='strip')out.push(...block.items)
  else if(block.type==='row')out.push(...block.items.filter(it=>it.kind==='image'))
  else if(block.type==='figure')out.push({src:block.src,alt:block.alt,caption:block.caption})
 }
 return out
})
const outline=computed(()=>[...prepared.value.filter(b=>b.id&&b.title).map(b=>({id:b.id,title:b.title})),...(props.links.length?[{id:'context',title:props.zh?'延伸閱讀':'Context & sources'}]:[])])
// 頁首影片：靜音自動循環（2026-09-18 哲宇：「未來都以這個模式為主」）。
// 尊重「減少動態」偏好：有這個偏好就停在海報，按了才播；離開視窗就暫停，回來再播（省電、省 CPU）。
const heroVideo=ref(null),heroPaused=ref(true),heroHd=ref(false)   // 寬螢幕或 Retina 才拿 HD 版（畫質夠，手機不白扛 17 MB）
const setHeroVideo=el=>{heroVideo.value=el||null}   // 在 v-for 裡的 ref 會變陣列，用函式 ref 拿單一元素
// 墊底圖（2026-09-25 動態設計「作品浮出」）：頁首影片／海報還沒到之前，先墊作品卡用的同一張封面（通常已在快取），
// 海報或影片第一格到了再 280ms 淡出。取代「標題出來了、主視覺是空黑框、圖再一次跳出來」；也是縮圖形變的落點。
// 預渲染與爬蟲不放（靜態 HTML 維持原本的海報）。海報已在快取就一開始視為就緒，不閃。
const underlay=typeof navigator!=='undefined'&&!/bot|crawler|spider|headless|prerender/i.test(navigator.userAgent)
const posterReady=b=>{if(!b||typeof Image==='undefined')return false;const url=b.poster;if(!url)return false;const im=new Image();im.src=url;return im.complete&&im.naturalWidth>0}
const heroSettled=ref(false)
const settleHero=()=>{heroSettled.value=true}
function armUnderlay(){
 const b=heroBlock.value
 if(!b||!underlay){heroSettled.value=true;return}
 if(posterReady(b)){heroSettled.value=true;return}
 heroSettled.value=false
 // 只在海報真的到了（或影片第一格到了，見 @loadeddata）才淡出；海報載不到就留著封面——總比空框好
 if(b.poster){const im=new Image();im.onload=settleHero;im.src=b.poster}
}
let heroIO=null,heroUserPaused=false
const reducedMotion=()=>typeof matchMedia==='function'&&matchMedia('(prefers-reduced-motion: reduce)').matches
const heroPlay=async()=>{const v=heroVideo.value;if(!v)return;try{await v.play();heroPaused.value=false}catch{heroPaused.value=true}}
const heroPause=()=>{const v=heroVideo.value;if(v)v.pause();heroPaused.value=true}
function toggleHero(){if(heroPaused.value){heroUserPaused=false;heroPlay()}else{heroUserPaused=true;heroPause()}}
function armHero(){if(typeof window!=='undefined')heroHd.value=(window.innerWidth||0)*(window.devicePixelRatio||1)>=2200;const v=heroVideo.value;if(heroIO){heroIO.disconnect();heroIO=null}if(!v)return;if(reducedMotion()){heroPaused.value=true;return}
 if(typeof IntersectionObserver==='function'){heroIO=new IntersectionObserver(entries=>{for(const e of entries){if(e.isIntersecting){if(!heroUserPaused)heroPlay()}else heroPause()}},{threshold:.15});heroIO.observe(v)}else heroPlay()}
armUnderlay()
onMounted(()=>nextTick(armHero))
watch(heroBlock,()=>{armUnderlay();nextTick(armHero)})
onBeforeUnmount(()=>{if(heroIO)heroIO.disconnect()})
// 動態格（loops）：一格一支無聲循環影片，一頁可能有八支——進視窗才給 src、離開就暫停，
// 開頁不一起下載；「減少動態」偏好下整格只放海報（跟頁首影片同一條規矩）。
const loopsStill=ref(false)
let loopIO=null
function armLoops(){
 if(loopIO){loopIO.disconnect();loopIO=null}
 if(reducedMotion()){loopsStill.value=true;return}
 const videos=document.querySelectorAll('.work-loop video[data-src]')
 if(!videos.length)return
 if(typeof IntersectionObserver!=='function'){for(const v of videos){v.src=v.dataset.src;v.play().catch(()=>{})}return}
 loopIO=new IntersectionObserver(entries=>{for(const e of entries){const v=e.target;if(e.isIntersecting){if(!v.getAttribute('src')){v.src=v.dataset.src;v.load()}v.play().catch(()=>{})}else if(!v.paused)v.pause()}},{rootMargin:'160px 0px'})
 for(const v of videos)loopIO.observe(v)
}
onMounted(()=>nextTick(armLoops))
watch(prepared,()=>nextTick(armLoops))
onBeforeUnmount(()=>{if(loopIO)loopIO.disconnect()})
// 有聲影片也自動播（2026-09-20 哲宇：「作品能自動播放的影片在作品頁要自動播放」）：進視窗靜音起播、離開暫停；
// 聲音要人按（瀏覽器政策：靜音才准自動播），按了「開聲音」就解靜音續播；使用者自己按暫停後不再自動續播；
// 減少動態偏好下不自動播（跟頁首影片、loops 同一條規矩）。資料層 `autoplay:false` 可關掉。
const soundOn=reactive({})
let videoIO=null
function armVideos(){
 if(videoIO){videoIO.disconnect();videoIO=null}
 if(reducedMotion())return
 if(typeof navigator!=='undefined'&&navigator.webdriver===true)return // 預渲染的無頭瀏覽器不播影片：播放／暫停事件的先後會讓 data-user-paused 時有時無
 const videos=document.querySelectorAll('.work-video video[data-autoplay]')
 if(!videos.length)return
 const playIt=v=>{if(v.dataset.userPaused)return;v.dataset.auto='1';v.play().catch(()=>{}).finally(()=>{delete v.dataset.auto})}
 if(typeof IntersectionObserver!=='function'){videos.forEach(playIt);return}
 videoIO=new IntersectionObserver(entries=>{for(const e of entries){const v=e.target;if(e.isIntersecting)playIt(v);else if(!v.paused){v.dataset.auto='1';v.pause()}}},{threshold:.35})
 for(const v of videos)videoIO.observe(v)
}
const videoPaused=e=>{const v=e.target;if(v.dataset.auto){delete v.dataset.auto;return}if(!v.ended)v.dataset.userPaused='1'}
const videoPlayed=e=>{if(!e.target.dataset.auto)delete e.target.dataset.userPaused}
// audio：點段落跳到那一秒並播放（2026-10-16 語音導聆）；preload none 時先等 metadata 再跳，正在播的段落亮起來
const audioEls={},audioNow=reactive({})
const setAudio=(id,el)=>{if(el)audioEls[id]=el;else delete audioEls[id]}
const audioTick=(id,e)=>{audioNow[id]=e.target.currentTime}
const clock=s=>{s=Math.max(0,Math.round(Number(s)||0));return Math.floor(s/60)+':'+String(s%60).padStart(2,'0')}
const audioChapter=(block,i)=>{const t=audioNow[block.id];if(t==null||t<=0)return false;const c=block.chapters,next=c[i+1];return t>=c[i].t&&(!next||t<next.t)}
function seekAudio(id,t){const a=audioEls[id];if(!a)return;const go=()=>{a.currentTime=t;audioNow[id]=t;a.play().catch(()=>{})};if(a.readyState>=1){go();return}a.addEventListener('loadedmetadata',go,{once:true});a.preload='metadata';a.load()}
function toggleSound(block,e){const v=e.currentTarget.parentElement.querySelector('video');if(!v)return;const on=!soundOn[block.id];soundOn[block.id]=on;v.muted=!on;if(on){delete v.dataset.userPaused;v.play().catch(()=>{})}}
onMounted(()=>nextTick(armVideos))
watch(prepared,()=>nextTick(armVideos))
onBeforeUnmount(()=>{if(videoIO)videoIO.disconnect()})
// 長條探索：橫向捲動＋滑鼠拖曳；下方一條細軌顯示現在看到整條布的哪一段。
const stripState=reactive({})
const stripScroll=(e,key)=>{const el=e.currentTarget;const max=el.scrollWidth-el.clientWidth;stripState[key]={pos:max>0?el.scrollLeft/el.scrollWidth:0,thumb:el.scrollWidth>0?el.clientWidth/el.scrollWidth:1}}
const stripPos=key=>Math.round((stripState[key]?.pos||0)*1000)/10
const stripThumb=key=>Math.round((stripState[key]?.thumb||1)*1000)/10
let drag=null
const stripDown=e=>{if(e.pointerType==='touch')return;const el=e.currentTarget;drag={el,x:e.clientX,left:el.scrollLeft,moved:false};el.classList.add('is-dragging');try{el.setPointerCapture(e.pointerId)}catch{}}
const stripMove=e=>{if(!drag||drag.el!==e.currentTarget)return;const dx=e.clientX-drag.x;if(Math.abs(dx)>2)drag.moved=true;drag.el.scrollLeft=drag.left-dx}
const stripUp=e=>{if(!drag)return;drag.el.classList.remove('is-dragging');try{drag.el.releasePointerCapture(e.pointerId)}catch{}drag=null}
function showImage(item,trigger){lightbox.value.open(allImages.value,Math.max(0,allImages.value.findIndex(i=>i.src===item.src)),trigger)}
function showRichImage(payload){lightbox.value.open(payload.images,payload.index,payload.trigger)}
</script>
<style>
.work-video-stage {
  padding:0 0 24px;
}
.work-video {
  margin:0;
  display:grid;
  gap:10px;
  justify-items:center;
  position:relative;
}
.work-video-sound {
  position:absolute;
  right:14px;
  top:14px;
  z-index:2;
  font-size:11px;
  letter-spacing:.05em;
  padding:7px 14px;
  background:rgba(0,0,0,.62);
  border:0;
  border-radius:var(--r-pill);
  color:var(--work-paper);
  cursor:pointer;
}
.work-video-sound[aria-pressed="true"] {
  background:var(--gold-tint);
  color:var(--gold);
}
.work-video video,.work-video img {
  display:block;
  max-width:100%;
  height:auto;
  background:#0b0b0b;
  max-height:min(86vh,1080px);
  width:auto;
}
.work-video-portrait video,.work-video-portrait img {
  max-height:min(88vh,1100px);
}
.work-video figcaption {
  display:flex;
  gap:16px;
  justify-content:space-between;
  width:100%;
  font-size:.82rem;
  color:var(--ink-2,#b4b4b0);
}
.work-video-pending {
  color:var(--gold,#d9c267);
}
.work-lineage-stage {
  padding:0 0 24px;
}
.work-lineage-stage .lin-block {
  gap:16px;
}
.work-lineage-stage .lin-block-lead {
  max-width:76ch;
}
.work-loops-stage {
  padding:0 0 24px;
}
.work-loops-grid {
  display:grid;
  grid-template-columns:repeat(2,minmax(0,1fr));
  gap:52px 32px;
}
.work-loops-cols-1 {
  grid-template-columns:1fr;
}
.work-loops-cols-3 {
  grid-template-columns:repeat(3,minmax(0,1fr));
}
.work-loops-cols-4 {
  grid-template-columns:repeat(4,minmax(0,1fr));
}
.work-loop {
  margin:0;
  min-width:0;
}
.work-loop video,.work-loop img {
  display:block;
  width:100%;
  height:auto;
  background:#050505;
}
.work-loops-note {
  font-size:13px;
  line-height:1.7;
  color:var(--work-muted);
  margin:28px 0 0;
  max-width:80ch;
}
@media(max-width:991px) {
  .work-loops-grid {
    gap:32px 18px;
  }
  .work-loops-cols-3,.work-loops-cols-4 {
    grid-template-columns:repeat(2,minmax(0,1fr));
  }
}
@media(max-width:480px) {
  .work-loops-grid {
    grid-template-columns:1fr;
  }
  .work-loops-cols-3,.work-loops-cols-4 {
    grid-template-columns:repeat(2,minmax(0,1fr));
  }
}
.work-record {
  --work-paper:#f2eee5;
  --work-muted:#b8b5ad;
  background:#080808;
  color:var(--work-paper);
  text-align:left;
  font-family:var(--font-body);
  padding:52px max(24px,5vw) 96px;
  max-width:1840px;
  margin:auto;
  overflow-wrap:anywhere;
}
.work-record a {
  color:inherit;
  text-underline-offset:5px;
}
.work-record a:hover {
  color:var(--gold);
}
.work-record a:focus-visible,.work-record button:focus-visible,.work-record summary:focus-visible {
  outline:2px solid var(--gold);
  outline-offset:5px;
}
.work-record-heading {
  display:grid;
  grid-template-columns:260px minmax(0,1fr);
  gap:72px;
  padding:12px 0 44px;
}
.work-record .back-link {
  color:var(--ink-3);
}
.work-back {
  display:inline-flex;
  align-items:center;
  gap:8px;
  font-size:13px;
  align-self:start;
  padding:8px 0;
}
/* 返回記號：2023 品牌圖示 Back（2026-10-04 取代字元 ←，文字保留） */
.work-back .brand-icon {
  width:18px;
  height:18px;
}
.work-kicker {
  display:flex;
  gap:24px;
  font-size:12px;
  text-transform:uppercase;
  letter-spacing:.1em;
  margin:0 0 20px;
  color:var(--work-muted);
}
.work-record h1 {
  font-family:var(--font-slab),var(--font-display);
  font-size:clamp(46px,5.5vw,88px);
  font-weight:500;
  line-height:1.05;
  letter-spacing:-.025em;
  margin:0;
  max-width:22ch;
}
.work-record h1:lang(zh-Hant) {
  font-family:var(--font-display);
  font-size:clamp(38px,4.6vw,72px);
  line-height:1.25;
  letter-spacing:0;
}
.work-subtitle {
  font-size:16px;
  color:var(--work-muted);
  margin:18px 0 0;
  max-width:70ch;
}
/* 基因條（2026-10-04；2026-10-05 改成標籤）：標題下一枚作品的「標本標籤」，從主視覺那張圖橫切一刀（GeneStrip.vue 的 label 模式）。
   哲宇 10/05：「現在看起來很像壞掉」。原本 760×10（76:1）浮在副標與主視覺中間、上下距差不多，暗作品左邊一段黑，讀起來是載到一半的進度條
   （2023 設計稿的載入條正好就是 1159×8 的基因條）。改成條碼比例 16:1（320×20，2023 Lab 卡約 17:1、作品保證卡也是一枚短條），
   左緣對齊標題，貼著標題那一組（上 28px）、離主視覺遠（下 60px），不跟圖連在一起；寬 320 只要 640 原圖像素（2 倍螢幕），
   才挑得出「整條有色、兩端不黑」的一段（gene-strips.json 的 tl），760 寬在 2 倍螢幕要 1520 像素，1920 寬的圖幾乎沒得裁。直角、不加框。 */
.work-gene {
  --gene-h:20px;
  width:320px;
  max-width:100%;
  margin-top:28px;
}
.work-record-heading.has-gene {
  padding-bottom:60px;
}
.work-cover {
  margin:0 0 80px;
}
.work-cover button,.work-gallery button {
  position:relative;
  display:block;
  width:100%;
  border:0;
  padding:0;
  background:#050505;
  cursor:zoom-in;
  color:var(--work-paper);
}
.work-cover img {
  display:block;
  width:auto;
  max-width:100%;
  height:auto;
  max-height:min(72vh,840px);
  margin:0 auto;
  object-fit:contain;
}
.work-image-hint {
  position:absolute;
  right:14px;
  bottom:14px;
  font-size:11px;
  letter-spacing:.05em;
  padding:7px 14px;
  background:rgba(0,0,0,.62);
  border-radius:var(--r-pill);
}
.work-record figcaption {
  font-size:13px;
  line-height:1.7;
  color:var(--work-muted);
  margin-top:12px;
  max-width:80ch;
}
.work-reading-layout {
  display:grid;
  grid-template-columns:260px minmax(0,880px);
  gap:72px;
  justify-content:space-between;
}
.work-record-aside {
  align-self:start;
  position:sticky;
  top:100px;
  max-height:calc(100vh - 130px);
  overflow-y:auto;
  padding-right:10px;
}
.work-record-aside nav {
  display:grid;
  gap:0;
  border-top:1px solid #38362f;
  padding:16px 0 28px;
}
.work-record-aside nav a {
  display:flex;
  gap:16px;
  padding:10px 0;
  font-size:13px;
  text-decoration:none;
  color:#b8b5ad;
}
.work-record-aside nav a span {
  font-size:10px;
  color:#7b776d;
  min-width:18px;
}
.work-record dl {
  margin:0;
  display:grid;
  gap:8px;
}
.work-record dt {
  font-size:11px;
  letter-spacing:.06em;
  color:#918d82;
  margin-top:20px;
}
.work-record dd {
  font-size:14px;
  line-height:1.65;
  margin:0;
}
.work-experience {
  display:block;
  font-size:13px;
  line-height:1.6;
  margin-top:32px;
  border-top:1px solid #38362f;
  padding-top:22px;
}
.work-mobile-index {
  display:none;
}
.work-reading {
  min-width:0;
  max-width:880px;
}
.work-text-section {
  margin:0 0 76px;
  scroll-margin-top:100px;
}
.work-section-label {
  font-size:12px;
  letter-spacing:.1em;
  text-transform:uppercase;
  color:#aaa396;
}
.work-record h2 {
  font-family:var(--font-slab),var(--font-display);
  font-size:clamp(30px,3vw,46px);
  font-weight:500;
  line-height:1.25;
  margin:0 0 28px;
}
.work-record-zh h2 {
  font-family:var(--font-display);
  font-size:clamp(26px,2.7vw,38px);
}
.work-reading p {
  font-size:18px;
  line-height:1.9;
  white-space:pre-line;
}
.work-intro p {
  font-size:clamp(20px,2vw,26px);
  line-height:1.8;
  color:#f2eee5;
  margin:0 0 1.3em;
}
.work-language-note {
  font-size:13px!important;
  color:#aaa396;
}
/* 當時的作品說明（originalHtml）：預設收起，全文仍在 HTML 裡給搜尋與預渲染讀（2026-10-18 哲宇定 A） */
.work-original-details > summary {
  list-style:none;
  cursor:pointer;
}
.work-original-details > summary::-webkit-details-marker {
  display:none;
}
.work-original-details > summary h2 {
  margin-bottom:12px;
}
.work-original-toggle {
  display:inline-block;
  margin-top:4px;
  padding-bottom:2px;
  border-bottom:1px solid #5b574d;
  font-size:13px;
  letter-spacing:.06em;
  color:#aaa396;
}
.work-original-details > summary:hover .work-original-toggle,
.work-original-details > summary:focus-visible .work-original-toggle {
  color:#f2eee5;
  border-color:#aaa396;
}
.work-original-details[open] > summary {
  margin-bottom:28px;
}
.work-original-details[open] .work-original-show,
.work-original-details:not([open]) .work-original-hide {
  display:none;
}
.work-gallery {
  margin:12px 0 100px;
  scroll-margin-top:100px;
}
.work-stage {
  margin:12px 0 100px;
  scroll-margin-top:100px;
}
.work-stage .work-gallery-heading {
  border-top:1px solid #38362f;
  padding:22px 0;
  display:flex;
  justify-content:space-between;
  font-size:12px;
  color:#aaa396;
}
.work-record .work-media-stage {
  margin:0 0 40px;
}
.work-media-stage iframe.work-loaded-media {
  aspect-ratio:16/10;
  min-height:min(76vh,880px);
}
@media(max-width:991px) {
  .work-media-stage iframe.work-loaded-media {
    aspect-ratio:4/3;
    min-height:0;
  }
}
.work-gallery-heading {
  border-top:1px solid #38362f;
  padding:22px 0;
  display:flex;
  justify-content:space-between;
  font-size:12px;
  color:#aaa396;
}
.work-gallery-grid {
  display:grid;
  grid-template-columns:repeat(2,minmax(0,1fr));
  gap:52px 32px;
}
.work-gallery figure {
  margin:0;
  min-width:0;
}
.work-gallery img {
  width:100%;
  height:clamp(260px,42vw,700px);
  object-fit:contain;
  display:block;
}
.work-gallery figure:first-child:nth-last-child(1) {
  grid-column:1/-1;
}
.work-gallery figure.work-gallery-wide {
  grid-column:1/-1;
}
.work-gallery-cols-3 {
  grid-template-columns:repeat(3,minmax(0,1fr));
}
.work-gallery-cols-3 img {
  height:clamp(200px,26vw,440px);
}
.work-gallery-cols-1 {
  grid-template-columns:minmax(0,1fr);
}
.work-record .work-quote {
  margin:0 0 76px;
  padding:0 0 0 1.4em;
  font-size:clamp(24px,2.4vw,34px);
  scroll-margin-top:100px;
}
.work-quote blockquote {
  margin:0;
}
.work-quote p {
  font-family:var(--font-slab),var(--font-display);
  font-size:1em;
  line-height:1.55;
  margin:0 0 .6em;
}
.work-record-zh .work-quote p {
  font-family:var(--font-display);
}
.work-quote figcaption {
  margin-top:14px;
  font-size:13px;
  color:var(--work-muted);
}
/* audio：語音導聆（2026-10-16《萬物公式》）。原生播放器拉滿正文欄；段落一行一個：時間（金、等寬數字）＋段名，說明另起一行；正在播的那段亮起來。 */
.work-record .work-audio audio {
  display:block;
  width:100%;
  margin:6px 0 18px;
  color-scheme:dark;
}
.work-record .work-audio-caption,
.work-record .work-audio-foot {
  font-size:14px;
  color:var(--work-muted);
  margin:0 0 10px;
}
.work-record .work-audio-foot {
  margin:16px 0 0;
}
.work-record .work-audio-chapters {
  list-style:none;
  margin:0;
  padding:0;
  border-top:1px solid rgba(255,255,255,.12);
}
.work-record .work-audio-chapters li {
  border-bottom:1px solid rgba(255,255,255,.12);
  padding:10px 0 11px;
}
.work-record .work-audio-chapters button {
  display:flex;
  align-items:baseline;
  gap:16px;
  width:100%;
  padding:0;
  background:none;
  border:0;
  color:inherit;
  font:inherit;
  text-align:left;
  cursor:pointer;
}
.work-record .work-audio-time {
  flex:0 0 3.2em;
  font-variant-numeric:tabular-nums;
  font-size:13px;
  letter-spacing:.04em;
  color:var(--gold);
}
.work-record .work-audio-name {
  font-size:16px;
}
.work-record .work-audio-chapters button:hover .work-audio-name,
.work-record .work-audio-chapters button:focus-visible .work-audio-name {
  color:var(--gold);
}
.work-record .work-audio-note {
  margin:4px 0 0 calc(3.2em + 16px);
  font-size:14px;
  line-height:1.7;
  color:var(--work-muted);
}
.work-record .work-audio-chapters li.is-current .work-audio-name {
  color:var(--gold);
}
.work-record .work-audio-chapters li.is-current {
  box-shadow:inset 2px 0 0 var(--gold);
  padding-left:12px;
}
@media (max-width:640px) {
  .work-record .work-audio-note {
    margin-left:0;
  }
}
/* poem：整首詩（2026-10-04《彼岸花》〈夢的彼岸〉）。比引言小一級、行距收緊，像排詩不是排標語；
   桌機 CSS 多欄（欄數由 poem-utils.mjs 依行數與最長一行算），一段不拆到兩欄；手機單欄、更緊湊。不收合。 */
.work-record .work-poem {
  margin:0 0 72px;
  scroll-margin-top:100px;
}
.work-record .work-poem-head {
  display:flex;
  flex-wrap:wrap;
  align-items:baseline;
  gap:4px 14px;
  margin:0 0 22px;
}
.work-poem-title {
  font-family:var(--font-display);
  font-size:15px;
  letter-spacing:.06em;
  color:var(--gold);
}
.work-record .work-poem-cite,
.work-record .work-poem-note {
  font-size:13px;
  color:var(--work-muted);
}
.work-record .work-poem-note {
  margin:18px 0 0;
}
.work-record .work-poem-label {
  margin:0 0 10px;
  font-size:12px;
  letter-spacing:.1em;
  text-transform:uppercase;
  color:#aaa396;
}
.work-poem-version + .work-poem-version {
  margin-top:30px;
}
.work-poem-body {
  column-gap:48px;
  font-family:var(--font-display);
  font-size:clamp(17px,1.35vw,20px);
  line-height:1.72;
}
.work-poem-version[lang="en"] .work-poem-body {
  font-family:var(--font-slab),var(--font-display);
  font-size:clamp(16px,1.25vw,18px);
  line-height:1.62;
}
.work-poem-cols-2 { columns:2; }
.work-poem-cols-3 { columns:3; }
.work-record .work-poem-stanza {
  break-inside:avoid;
  margin:0 0 1em;
  font-size:1em;
  line-height:inherit;
}
.work-inline-figure {
  margin:0 0 76px;
  scroll-margin-top:100px;
}
.work-inline-figure button {
  position:relative;
  display:block;
  width:100%;
  border:0;
  padding:0;
  background:#050505;
  cursor:zoom-in;
  color:var(--work-paper);
}
.work-inline-figure img {
  display:block;
  width:100%;
  height:auto;
  max-height:min(70vh,760px);
  object-fit:contain;
}
.work-credits dl {
  display:grid;
  grid-template-columns:minmax(120px,210px) minmax(0,1fr);
  gap:0;
  border-top:1px solid #38362f;
}
.work-credits dt {
  font-size:13px;
  color:#aaa396;
  margin:0;
  padding:14px 20px 14px 0;
  border-bottom:1px solid #262520;
}
.work-credits dd {
  font-size:15px;
  line-height:1.6;
  margin:0;
  padding:14px 0;
  border-bottom:1px solid #262520;
}
.work-gallery figure.work-gallery-wide img {
  height:auto;
  max-height:min(60vh,700px);
}
.work-source-list {
  list-style:none;
  padding:0;
  margin:0;
  border-top:1px solid #38362f;
}
.work-source-list li {
  padding:18px 0;
  border-bottom:1px solid #38362f;
  font-size:15px;
  line-height:1.6;
}
.work-contact {
  border-top:1px solid #38362f;
  padding:32px 0 64px;
}
.work-contact p {
  font-size:13px;
  color:#aaa396;
}
.work-contact a {
  font-family:var(--font-slab),var(--font-display);
  font-size:28px;
}
.work-related {
  border-top:1px solid #38362f;
  padding-top:48px;
  margin-top:40px;
}
.work-related-grid {
  display:grid;
  grid-template-columns:repeat(3,minmax(0,1fr));
  gap:32px;
}
.work-related-grid a {
  text-decoration:none;
}
.work-related-grid img {
  width:100%;
  height:280px;
  object-fit:contain;
  background:#050505;
}
.work-related-grid p {
  font-size:12px;
  color:#aaa396;
  margin:16px 0 8px;
}
.work-related-grid h3 {
  font-family:var(--font-slab),var(--font-display);
  font-size:25px;
  font-weight:500;
  margin:0;
  line-height:1.3;
}
.work-neighbours {
  display:flex;
  justify-content:space-between;
  gap:40px;
  border-top:1px solid #38362f;
  padding-top:32px;
  margin-top:64px;
}
.work-neighbours a {
  display:grid;
  gap:12px;
  text-decoration:none;
  max-width:45%;
}
.work-neighbours span {
  font-size:12px;
  color:#aaa396;
}
.work-neighbours strong {
  font-size:18px;
  font-weight:400;
  line-height:1.5;
}
.work-next {
  text-align:right;
  margin-left:auto;
}
.work-record iframe.work-loaded-media {
  display:block;
  width:100%;
  aspect-ratio:16/9;
  border:0;
}
.work-record .work-media {
  display:block;
  margin:40px 0;
  padding:0;
  border:0;
  min-height:0;
  background:none;
}
.work-media figcaption {
  display:flex;
  flex-wrap:wrap;
  align-items:center;
  justify-content:space-between;
  gap:16px;
  padding:14px 0;
  font-size:13px;
  color:var(--work-muted);
}
@media(max-width:1100px) {
  .work-record-heading,.work-reading-layout {
    grid-template-columns:210px minmax(0,1fr);
    gap:40px;
  }
  .work-related-grid img {
    height:220px;
  }
}
@media(max-width:991px) {
  .work-record {
    padding:32px 22px 64px;
  }
  .work-record-heading {
    display:block;
    padding:0 0 30px;
  }
  .work-back {
    display:inline-flex;
    margin-bottom:28px;
  }
  .work-kicker {
    margin-bottom:15px;
  }
  .work-record-heading.has-gene {
    padding-bottom:40px;
  }
  .work-gene {
    --gene-h:16px;
    width:240px;
    margin-top:22px;
  }
  .work-record h1 {
    font-size:clamp(38px,7vw,64px);
  }
  .work-record h1:lang(zh-Hant) {
    font-size:clamp(32px,6vw,48px);
  }
  .work-cover {
    margin-bottom:32px;
  }
  .work-cover img {
    height:auto;
    max-height:min(72vh,540px);
  }
  .work-reading-layout {
    display:block;
  }
  .work-record-aside {
    display:none;
  }
  .work-mobile-index {
    display:block;
    border-top:1px solid #38362f;
    border-bottom:1px solid #38362f;
    padding:18px 0;
    margin-bottom:44px;
    font-size:14px;
  }
  .work-mobile-index summary {
    cursor:pointer;
  }
  .work-mobile-index nav {
    display:grid;
    gap:15px;
    padding-top:24px;
  }
  .work-mobile-index dl {
    padding:4px 0 26px;
  }
  .work-text-section {
    margin-bottom:56px;
  }
  .work-reading p {
    font-size:17px;
  }
  .work-intro p {
    font-size:21px;
  }
  .work-gallery {
    margin-bottom:60px;
  }
  .work-gallery-grid {
    gap:32px 18px;
  }
  .work-gallery img {
    height:clamp(240px,65vw,500px);
  }
  .work-gallery-cols-3 {
    grid-template-columns:repeat(2,minmax(0,1fr));
  }
  .work-record .work-quote {
    font-size:clamp(20px,5vw,26px);
  }
  .work-poem-cols-2,
  .work-poem-cols-3 {
    columns:1;
  }
  .work-poem-body {
    font-size:16px;
    line-height:1.62;
  }
  .work-poem-version[lang="en"] .work-poem-body {
    font-size:15px;
    line-height:1.55;
  }
  .work-record .work-poem-stanza {
    margin-bottom:.85em;
  }
  .work-record .work-poem {
    margin-bottom:56px;
  }
  .work-credits dl {
    grid-template-columns:1fr;
  }
  .work-credits dt {
    padding-bottom:0;
    border-bottom:0;
  }
  .work-credits dd {
    padding-top:4px;
  }
  .work-related-grid {
    grid-template-columns:repeat(2,minmax(0,1fr));
    gap:28px 20px;
  }
  .work-related-grid img {
    height:190px;
  }
  .work-neighbours {
    gap:24px;
  }
  .work-neighbours strong {
    font-size:16px;
  }
}
@media(max-width:480px) {
  .work-record {
    padding-left:18px;
    padding-right:18px;
  }
  .work-gallery-grid {
    grid-template-columns:1fr;
  }
  .work-related-grid {
    grid-template-columns:1fr;
  }
  .work-related-grid img {
    height:260px;
  }
  .work-subtitle {
    font-size:14px;
  }
  .work-neighbours {
    display:grid;
    grid-template-columns:1fr 1fr;
    gap:18px;
  }
  .work-neighbours a {
    max-width:100%;
  }
}
.work-hero {
  margin:0 0 72px;
  scroll-margin-top:100px;
}
.work-hero-figure {
  margin:0;
  position:relative;
}
.work-hero-figure video,.work-hero-figure img {
  display:block;
  width:100%;
  height:auto;
  background:#050505;
  cursor:pointer;
}
.work-hero-toggle {
  position:absolute;
  right:14px;
  bottom:52px;
  font-size:11px;
  letter-spacing:.05em;
  padding:7px 14px;
  background:rgba(0,0,0,.62);
  border:0;
  border-radius:var(--r-pill);
  color:var(--work-paper);
  cursor:pointer;
}
.work-hero-figure figcaption {
  display:flex;
  flex-wrap:wrap;
  gap:8px 24px;
  justify-content:space-between;
  max-width:none;
}
.work-hero-note {
  color:#8f8b80;
}
.work-strip-stage .work-gallery-heading span:last-child {
  text-align:right;
}
.work-strip {
  margin:0 0 44px;
}
.work-strip-scroll {
  overflow-x:auto;
  overflow-y:hidden;
  overscroll-behavior-x:contain;
  scrollbar-width:none;
  cursor:grab;
  background:#050505;
  -webkit-overflow-scrolling:touch;
}
.work-strip-scroll::-webkit-scrollbar {
  display:none;
}
.work-strip-scroll.is-dragging {
  cursor:grabbing;
  user-select:none;
}
.work-strip-scroll:focus-visible {
  outline:2px solid var(--gold);
  outline-offset:4px;
}
.work-strip-scroll img {
  display:block;
  width:auto;
  max-width:none;
  height:480px;
}
.work-strip-bar {
  position:relative;
  height:2px;
  margin-top:10px;
  background:#2a2823;
}
.work-strip-bar span {
  position:absolute;
  top:0;
  height:100%;
  background:var(--gold);
  min-width:6px;
}
.work-strip figcaption {
  display:flex;
  justify-content:space-between;
  gap:16px;
  max-width:none;
}
.work-strip-open {
  font:inherit;
  font-size:13px;
  background:none;
  border:0;
  color:inherit;
  cursor:pointer;
  text-underline-offset:5px;
  text-decoration:underline;
}
.work-strip-open:hover {
  color:var(--gold);
}
.work-figure-stage {
  margin:12px 0 100px;
  scroll-margin-top:100px;
}
.work-figure-stage button {
  position:relative;
  display:block;
  width:100%;
  border:0;
  padding:0;
  background:#050505;
  cursor:zoom-in;
  color:var(--work-paper);
}
.work-figure-stage img {
  display:block;
  width:100%;
  height:auto;
  max-height:min(80vh,960px);
  object-fit:contain;
}
.work-stage-prose {
  max-width:1180px;
  margin:0 auto 96px;
  padding:0;
  scroll-margin-top:100px;
}
.work-stage-prose p {
  font-size:18px;
  line-height:1.9;
  white-space:pre-line;
}
.work-stage-prose.work-quote {
  padding-left:1.4em;
}
.work-columns-grid {
  display:grid;
  grid-template-columns:repeat(2,minmax(0,1fr));
  gap:40px 56px;
}
.work-column h3 {
  font-family:var(--font-slab),var(--font-display);
  font-size:22px;
  font-weight:500;
  line-height:1.35;
  margin:0 0 16px;
}
.work-record-zh .work-column h3 {
  font-family:var(--font-display);
  font-size:20px;
}
.work-column p {
  font-size:17px;
  line-height:1.85;
  margin:0 0 1.1em;
  white-space:pre-line;
}
@media(max-width:991px) {
  .work-hero {
    margin-bottom:40px;
  }
  .work-hero-toggle {
    bottom:auto;
    top:12px;
  }
  .work-strip-scroll img {
    height:300px!important;
  }
  .work-strip {
    margin-bottom:32px;
  }
  .work-columns-grid {
    grid-template-columns:1fr;
    gap:28px;
  }
  .work-stage-prose {
    margin-bottom:64px;
  }
}
/* 2026-09-18 策展式版面：圖依自己的比例填滿格子（不留黑邊）、直欄節奏收緊、拼貼列 */
.work-gallery-natural img {
  height:auto!important;
  object-fit:cover;
}
.work-gallery-fit-natural .work-gallery-grid {
  gap:28px 22px;
  align-items:start;
}
.work-gallery-fit-natural.work-gallery-cols-3 .work-gallery-grid,.work-gallery-fit-natural .work-gallery-cols-3 {
  gap:24px 18px;
}
.work-row-stage {
  margin:12px 0 72px;
}
.work-row-grid {
  display:grid;
  gap:28px 24px;
}
.work-row-image {
  margin:0;
  min-width:0;
}
.work-row-image button {
  position:relative;
  display:block;
  width:100%;
  border:0;
  padding:0;
  background:#050505;
  cursor:zoom-in;
  color:var(--work-paper);
}
.work-row-image img {
  display:block;
  width:100%;
  height:auto;
  object-fit:cover;
}
.work-row-text {
  min-width:0;
  align-self:center;
}
.work-row-text h3 {
  font-family:var(--font-slab),var(--font-display);
  font-size:clamp(22px,2vw,30px);
  font-weight:500;
  line-height:1.3;
  margin:0 0 16px;
}
.work-record-zh .work-row-text h3 {
  font-family:var(--font-display);
  font-size:clamp(20px,1.8vw,26px);
}
.work-row-text p {
  font-size:16px;
  line-height:1.85;
  margin:0 0 1em;
  white-space:pre-line;
  color:#ddd9cf;
}
.work-record .work-stage {
  margin:12px 0 72px;
}
.work-record .work-gallery {
  margin:12px 0 72px;
}
.work-record .work-text-section {
  margin:0 0 60px;
}
.work-record .work-hero {
  margin:0 0 56px;
}
.work-record .work-stage-prose {
  margin-bottom:80px;
}
.work-record .work-quote {
  margin-bottom:64px;
}
.work-record .work-gallery-grid {
  gap:36px 26px;
}
.work-record .work-cover {
  margin-bottom:64px;
}
.work-hero-figure video,.work-hero-figure img {
  width:100%;
  height:auto;
}
/* 馬賽克畫廊（2026-09-19，The Last Input 現場照）：12 欄格子、格子自己 3:2，大格 6×2、直幅 3×2、小格 3×1；dense 填洞。圖說留給燈箱，署名寫在 note。
   2026-09-28 列高改由格子寬算（容器查詢單位），格子不再帶 aspect-ratio：原本兩列高的格子被 aspect-ratio 從「兩列＋一道格距」的高度反推寬度，
   比欄寬多出 6px，格距變成 6／3px、最右一格凸出網格。現在列高＝小格寬 × 2/3（12 欄：c=(W−132)/12，h=2c+16），每一格都貼齊欄線，大格與直幅只差格距那幾 px 由 cover 吃掉。 */
.work-gallery-mosaic {
  container-type:inline-size;
}
.work-gallery-mosaic .work-gallery-grid {
  grid-template-columns:repeat(12,minmax(0,1fr));
  grid-auto-rows:calc((100cqw - 132px) / 6 + 16px);
  grid-auto-flow:dense;
  gap:12px;
  align-items:stretch;
}
.work-gallery-mosaic figure {
  grid-column:span var(--s,3);
  grid-row:span var(--r,1);
  min-width:0;
  min-height:0;
}
.work-gallery-mosaic button {
  height:100%;
}
.work-gallery-mosaic img {
  width:100%;
  height:100%!important;
  object-fit:cover;
  aspect-ratio:auto;
}
.work-gallery-note {
  margin:18px 0 0;
  font-size:13px;
  color:var(--work-muted);
  line-height:1.6;
}
/* 原生流程圖（2026-09-19）：階段卡片一排，色調由資料給（秩序金／混沌紅／循環之外藍），卡片之間細線接續；acts 變體有條目與「人／世界」兩行，chain 變體只有名稱與說明。 */
.work-flow-stage {
  margin:12px 0 88px;
}
.work-flow {
  list-style:none;
  margin:0;
  padding:0;
  display:grid;
  grid-template-columns:repeat(6,minmax(0,1fr));
  gap:18px;
  counter-reset:flow;
}
.work-flow-step {
  --tone:var(--work-muted);
  position:relative;
  display:flex;
  flex-direction:column;
  gap:14px;
  padding:22px 20px 20px;
  border:0;
  border-radius:var(--r-lg);
  background:var(--bg1);
  min-width:0;
}
.work-flow-step+.work-flow-step::before {
  content:"";
  position:absolute;
  left:-18px;
  top:34px;
  width:18px;
  height:1px;
  background:#38362f;
}
.work-flow-step+.work-flow-step::after {
  content:"";
  position:absolute;
  left:-7px;
  top:31px;
  width:5px;
  height:5px;
  border-right:1px solid #5a574e;
  border-bottom:1px solid #5a574e;
  transform:rotate(-45deg);
}
.work-flow-head {
  display:flex;
  flex-direction:column;
  gap:8px;
}
.work-flow-index::before {
  content:"";
  display:inline-block;
  width:6px;
  height:6px;
  margin-right:8px;
  border-radius:50%;
  background:var(--tone);
  vertical-align:1px;
}
.work-flow-index {
  font-size:11px;
  letter-spacing:.14em;
  text-transform:uppercase;
  color:#7d786c;
  font-variant-numeric:tabular-nums;
}
.work-flow-name {
  margin:0;
  font-family:var(--font-display);
  font-weight:500;
  font-size:clamp(19px,1.5vw,24px);
  line-height:1.3;
  color:var(--tone);
}
.work-flow-note {
  margin:0;
  font-size:14px;
  line-height:1.7;
  color:var(--work-muted);
  white-space:pre-line;
}
.work-flow-items {
  list-style:none;
  margin:0;
  padding:0;
  display:flex;
  flex-direction:column;
  gap:8px;
}
.work-flow-items li {
  font-size:14px;
  line-height:1.6;
  padding:9px 12px;
  border:0;
  border-radius:var(--r-md);
  background:var(--tint-1);
  color:var(--work-paper);
}
.work-flow-items li.is-key {
  background:color-mix(in srgb, var(--tone) 16%, transparent);
  color:var(--work-paper);
}
.work-flow-hands {
  margin:auto 0 0;
  padding-top:14px;
  border-top:1px dashed #262520;
  display:flex;
  flex-direction:column;
  gap:7px;
  font-size:13px;
  line-height:1.55;
}
.work-flow-hands p {
  margin:0;
  display:grid;
  grid-template-columns:2.6em minmax(0,1fr);
  gap:0 8px;
  align-items:baseline;
  color:var(--work-muted);
}
.work-flow-hands p span:first-child {
  color:#7d786c;
  font-size:11px;
  letter-spacing:.1em;
  text-transform:uppercase;
}
.work-flow-foot {
  display:flex;
  flex-wrap:wrap;
  gap:14px 32px;
  align-items:center;
  justify-content:space-between;
  margin-top:22px;
}
.work-flow-legend {
  list-style:none;
  margin:0;
  padding:0;
  display:flex;
  flex-wrap:wrap;
  gap:8px 24px;
  font-size:13px;
  color:var(--work-muted);
}
.work-flow-legend li::before {
  content:"";
  display:inline-block;
  width:10px;
  height:10px;
  margin-right:9px;
  vertical-align:-1px;
  background:var(--tone,#aaa396);
}
.work-flow-loop {
  margin:0;
  font-size:13px;
  color:#8fd0ff;
  padding:10px 14px;
  border:0;
  border-radius:var(--r-md);
  background:rgba(143,208,255,.1);
}
.work-flow-loop::before {
  content:"↺";
  margin-right:9px;
}
.work-flow-chain .work-flow {
  gap:14px;
}
.work-flow-chain .work-flow-step {
  padding:18px 18px 16px;
  gap:10px;
}
.work-flow-chain .work-flow-name {
  font-size:clamp(17px,1.25vw,20px);
}
@media(max-width:1200px) {
  .work-flow {
    grid-template-columns:repeat(3,minmax(0,1fr));
  }
  .work-flow-step:nth-child(3n+1)::before,.work-flow-step:nth-child(3n+1)::after {
    display:none;
  }
}
@media(max-width:991px) {
  .work-gallery-mosaic .work-gallery-grid {
    grid-template-columns:repeat(6,minmax(0,1fr));
    grid-auto-rows:calc((100cqw - 40px) / 3 + 10.667px);
    gap:8px;
  }
  .work-flow-stage {
    margin-bottom:56px;
  }
}
@media(max-width:640px) {
  .work-flow {
    grid-template-columns:repeat(2,minmax(0,1fr));
    gap:12px;
  }
  .work-flow-step:nth-child(3n+1)::before,.work-flow-step:nth-child(3n+1)::after {
    display:block;
  }
  .work-flow-step:nth-child(2n+1)::before,.work-flow-step:nth-child(2n+1)::after {
    display:none;
  }
  .work-flow-step {
    padding:16px 14px 14px;
  }
  .work-flow-items li {
    font-size:13px;
    padding:8px 10px;
  }
  .work-flow-foot {
    flex-direction:column;
    align-items:flex-start;
  }
}
@media(max-width:991px) {
  .work-row-grid {
    grid-template-columns:repeat(var(--row-mobile-cols,1),minmax(0,1fr))!important;
  }
  .work-row-image {
    grid-column:auto!important;
  }
  .work-row-text {
    grid-column:1/-1!important;
  }
  .work-record .work-stage,.work-record .work-gallery {
    margin-bottom:48px;
  }
  .work-record .work-text-section {
    margin-bottom:44px;
  }
  .work-gallery-natural img {
    height:auto!important;
  }
}
.work-icon-arrow {
  display:inline-block;
  vertical-align:-1px;
  margin-left:2px;
}
.work-image-hint .work-icon-arrow {
  vertical-align:-2px;
}
.work-table-scroll {
  overflow-x:auto;
}
.work-table table {
  width:100%;
  border-collapse:collapse;
  font-size:15px;
  line-height:1.6;
}
.work-table th {
  text-align:left;
  font-weight:500;
  font-size:12px;
  letter-spacing:.06em;
  color:#aaa396;
  padding:0 18px 12px 0;
  border-bottom:1px solid #38362f;
  white-space:nowrap;
}
.work-table td {
  vertical-align:top;
  padding:13px 18px 13px 0;
  border-bottom:1px solid #262520;
  min-width:120px;
}
.work-table td:first-child {
  color:var(--work-paper);
  white-space:nowrap;
}
.work-table td:last-child {
  color:#c9c5ba;
}
.work-table-note {
  font-size:13px;
  line-height:1.7;
  color:var(--work-muted);
  margin:16px 0 0;
  max-width:80ch;
}
@media(max-width:991px) {
  .work-table table {
    font-size:14px;
  }
  .work-table td:first-child {
    white-space:normal;
  }
}
</style>
